import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { catchError, finalize, Subscription, tap } from 'rxjs';
import { SvcUserAvatarService } from 'projects/lib-shared-core/src/lib/services/user-avatar/svc-user-avatar.service';
import { SvcUserInfo } from 'projects/lib-shared-core/src/lib/services/user-avatar/user-info.model';
import { ISvcUserAvatar } from 'projects/lib-shared-component/src/lib/svc-user-avatar/interfaces/svc-user-avatar.interface';
import { normalizeUserAvatarSize } from 'projects/lib-shared-component/src/lib/svc-user-avatar/svc-user-avatar-helpers';

@Component({
  selector: 'svc-smart-user-avatar',
  templateUrl: './svc-smart-user-avatar.component.html',
  styleUrls: ['./svc-smart-user-avatar.component.scss'],
})
export class SvcSmartUserAvatarComponent implements OnChanges, OnDestroy {
  @Input('ids') public set setIds(value: string | string[] | { usersId: string[]; teamsIds: number[]; areasIds: number[] }) {
    value = value ?? [];
    let userIds = [],
      teamsIds = [],
      areasIds = [];
    if (Array.isArray(value) || typeof value === 'string') {
      userIds = (Array.isArray(value) ? value : [value]).filter((v) => !!v);
      teamsIds = [];
      areasIds = [];
    } else {
      userIds = value?.usersId?.filter((v) => !!v) ?? [];
      teamsIds = value?.teamsIds?.filter((v) => !!v) ?? [];
      areasIds = value?.areasIds?.filter((v) => !!v) ?? [];
    }
    if (JSON.stringify({ userIds, teamsIds, areasIds }) !== JSON.stringify({ userIds: this.userIds, teamsIds: this.teamsIds, areasIds: this.areasIds })) {
      this.userIds = userIds;
      this.teamsIds = teamsIds;
      this.areasIds = areasIds;
      this.length = (userIds ?? []).length + (teamsIds ?? []).length + (areasIds ?? []).length;
      this.isSingle = this.length === 1;
      this.isMultiple = this.length > 1;
      this.#checkSize();
      this.#loadUserInfo();
    }
  }
  @Input() size: number = 30;
  @Input() maxSize: number = 1;
  @Input() borderColor: string = 'white';
  @Input() showNameTooltip = false;
  @Input() highDefinition = false;
  @Input() fallbackName: string | null = null;
  @Input() fallbackInitialsColor: string | null = null;
  @Output() public onLoadingChange = new EventEmitter<boolean>();

  #service = inject(SvcUserAvatarService);
  #cdr = inject(ChangeDetectorRef);
  #requestSubscription: Subscription;

  public internalSize: number = this.size;
  public isLoading: boolean = false;
  public isSingle: boolean = false;
  public isMultiple: boolean = false;
  protected infos: SvcUserInfo[] = [];
  protected avatars: ISvcUserAvatar[] = [];
  protected length: number = 0;
  protected userIds: string[] = [];
  protected teamsIds: number[] = [];
  protected areasIds: number[] = [];

  public info: SvcUserInfo;
  public avatar: ISvcUserAvatar;

  get fallbackAvatar(): ISvcUserAvatar | null {
    const name = `${this.fallbackName ?? ''}`.trim();
    if (!name) return null;
    return {
      name,
      picture: '',
      initialsColor: this.fallbackInitialsColor ?? undefined,
      size: this.internalSize,
      type: 1,
    };
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('size' in changes || 'ids' in changes) {
      this.#checkSize();
      this.#applySizeInCurrentAvatars();
    }
    if ('ids' in changes && JSON.stringify(changes.ids.previousValue ?? []) !== JSON.stringify(changes.ids.currentValue ?? [])) {
      this.#loadUserInfo();
    }
  }

  public refresh(): void {
    if (!this.isLoading) {
      this.#loadUserInfo();
    }
  }

  #checkSize(): void {
    this.internalSize = this.size;
    this.size = normalizeUserAvatarSize(this.size);
  }

  #loadUserInfo(): void {
    this.#requestSubscription?.unsubscribe();
    if (this.length > 0) {
      this.isLoading = true;
      this.#requestSubscription = this.#service
        .getUserInfoByIds({
          userIds: this.userIds,
          teamIds: this.teamsIds,
          areaIds: this.areasIds,
        })
        .pipe(
          tap((res) => {
            this.infos = res;
            this.avatars = this.infos.map(
              (ui) =>
                <ISvcUserAvatar>{
                  name: ui.name,
                  picture: ui.pictureFile,
                  pictureHQ: ui.pictureFileNormalSize,
                  initialsColor: ui.initialColor,
                  type: Math.max(ui.type, 1),
                  size: this.internalSize,
                }
            );
            this.info = this.infos?.length > 0 ? this.infos[0] : null;
            this.avatar = this.avatars?.length > 0 ? this.avatars[0] : null;
            this.#cdr.markForCheck();
          }),
          catchError((error) => {
            this.infos = [];
            this.avatars = [];
            this.info = null;
            this.avatar = null;
            this.#cdr.markForCheck();
            return error;
          }),
          finalize(() => {
            this.#requestSubscription = null;
            this.isLoading = false;
            this.#cdr.markForCheck();
          })
        )
        .subscribe();
    }
  }

  #applySizeInCurrentAvatars(): void {
    this.avatars = this.avatars?.map((a) => ({
      ...a,
      size: this.internalSize,
    }));
    this.avatar = this.avatars?.length > 0 ? this.avatars[0] : null;
  }

  ngOnDestroy(): void {}
}
